<template>
  <div :class="{ 'booking-loading': showLoading }">
    <v-progress-circular
      v-show="showLoading"
      :size="70"
      :width="7"
      color="primary"
      indeterminate
      class="centered"
    ></v-progress-circular>
    <div class="row">
      <div class="col text-right align-self-end">
        <button
          @click="() => $router.push({ name: 'DetailBooking' })"
          class="btn btn-outline-primary"
        >
          Create
        </button>
      </div>
    </div>
    <div class="row">
      <div class="col-3">
        <BrandDropdown
          v-model="brandSelected"
          @setBrand="setBrand"
          @setBrands="setBrands"
        ></BrandDropdown>
      </div>
      <div class="col-3">
        <StoreDropdown
          v-model="storeSelected"
          :brandCode="brandSelected ? brandSelected.id : null"
          @setStore="setStore"
          @setStores="setStores"
          @setLoading="setStoresLoading"
        ></StoreDropdown>
      </div>
      <div class="col-3">
        <StatusDropdown
          v-model="statusSelected"
          @setStatus="setStatus"
        ></StatusDropdown>
      </div>
      <div class="col-3">
        Date
        <date-picker
          v-bind="$attrs"
          v-model="date"
          format="D MMMM yyyy"
          lang="en"
          :value="date"
          @change="date = $event"
        />
      </div>
      <div class="col-3">
        <RoomDropdown
          v-model="roomSelected"
          :storeCode="storeSelected ? storeSelected.id : null"
          @setRoom="setRoom"
          @setLoading="setRoomsLoading"
        ></RoomDropdown>
      </div>
      <div class="col-3">
        <StaffDropdown
          v-model="staffSelected"
          :storeCode="storeSelected ? storeSelected.id : null"
          @setStaff="setStaff"
          @setLoading="setStaffsLoading"
        ></StaffDropdown>
      </div>
      <div class="col-3">
        <label for="keywordInput">Keyword</label>
        <input
          id="keywordInput"
          type="text"
          v-model="keyword"
          class="form-control"
          style="margin: 0.7rem 0"
        />
      </div>
    </div>

    <div class="d-flex justify-content-end">
      <div class="p-2 align-self-center">
        <button
          class="btn btn-outline-secondary"
          style="width: 100%; max-width: 131px"
          @click="resetFilter"
        >
          Reset
        </button>
      </div>
      <div class="p-2 align-self-center">
        <button
          class="btn btn-outline-primary"
          style="width: 100%; max-width: 131px"
          @click="searchBookings"
        >
          OK
        </button>
      </div>
    </div>
    <div class="mt-3">
      <div class="row">
        <div class="d-flex align-items-center justify-content-start col">
          <span class="mr-2">Show</span>
          <b-form-select
            class="form-control w-auto"
            v-model="perPage"
            :options="pageOptions"
            @change="onChangeEntries"
          ></b-form-select>
          <span class="ml-2">entries</span>
        </div>
      </div>
      <b-table
        class="table table-responsive table-bordered mt-5"
        hover
        responsive
        :fields="fields"
        :items="listBookings"
        :per-page="perPage"
        :sort-by.sync="sort.key"
        :sort-desc.sync="sort.type === 'desc'"
        @sort-changed="sortCompare"
      >
        <template #table-caption v-if="listBookings.length === 0">
          <h5 class="text-center pt-10">There is no bookings</h5>
        </template>
        <template #cell(action)="data">
          <div class="d-flex justify-content-evenly">
            <router-link
              :to="{
                name: 'DetailBooking',
                query: { id: data.item.id },
              }"
              class="btn btn-outline-primary"
              >Detail
            </router-link>
          </div>
        </template>
      </b-table>
      <b-pagination
        v-if="isPageReady"
        :total-rows="totalItems"
        v-model="currentPage"
        :per-page="perPage"
        class="my-0"
        aria-controls="my-table"
        @input="changePage"
      ></b-pagination>
    </div>
  </div>
</template>

<script>
import BrandDropdown from "components/Booking/BrandDropdown";
import StoreDropdown from "components/Booking/StoreDropdown";
import StatusDropdown from "components/Booking/StatusDropdown";
import RoomDropdown from "components/Booking/RoomDropdown";
import StaffDropdown from "components/Booking/StaffDropdown";
import DatePicker from "components/Datepicker/Datepicker.vue";
import DefaultListViewData from "@/views/defaultListViewData";
import BookingService from "services/api/booking.service";
import { BookingStorageService } from "services/storage.service";
import fields from "./listBookingFields.json";

export default {
  components: {
    DatePicker,
    BrandDropdown,
    StoreDropdown,
    StatusDropdown,
    RoomDropdown,
    StaffDropdown,
  },
  data() {
    return {
      ...DefaultListViewData,
      isLoading: false,
      isStoreLoading: false,
      isRoomLoading: false,
      isStaffLoading: false,
      brandSelected: null,
      brandItems: 0,
      storeSelected: null,
      storeItems: 0,
      roomSelected: null,
      staffSelected: null,
      statusSelected: null,
      date: null,
      keyword: null,
      isSubmit: false,
      message: null,
      isMounted: true,
      listBookings: [],
      fields,
      sort: { key: "", type: "desc" },
      isPageReady: false,
    };
  },
  computed: {
    showLoading() {
      return (
        this.isLoading ||
        this.isStoreLoading ||
        this.isRoomLoading ||
        this.isStaffLoading
      );
    },
  },
  mounted() {
    this.currentPage =
      JSON.parse(BookingStorageService.getStoredCurrentPage()) || 1;
    let storedParams =
      JSON.parse(BookingStorageService.getStoredParams()) || null;
    if (storedParams) {
      this.brandSelected = storedParams.brandSelected || null;
      this.storeSelected = storedParams.storeSelected || null;
      this.statusSelected = storedParams.statusSelected || null;
      this.date = storedParams.date || null;
      this.roomSelected = storedParams.roomSelected || null;
      this.staffSelected = storedParams.staffSelected || null;
      this.keyword = storedParams.keyword || null;
      this.perPage = storedParams.limit || 10;
      this.sort.key = storedParams.sortColumn;
      this.sort.type = storedParams.sortType;
    }
    this.$nextTick(() => {
      setTimeout(() => {
        this.getBookings();
      }, 0);
    });
  },
  methods: {
    async getBookings() {
      this.isLoading = true;
      const param = this.getParams();
      await BookingStorageService.setStoredParams(this.listStoredParams());
      await BookingStorageService.setStoredCurrentPage(this.currentPage);

      await BookingService.getBookings(param)
        .then(async (res) => {
          const data = res.data;
          this.totalItems = data.totalItems;
          this.listBookings = data.items;
          if (this.listBookings.isEmpty) {
            this.currentPage = 1;
            await BookingStorageService.setStoredCurrentPage(1);
          }
          this.isPageReady = true;
          this.isLoading = false;
        })
        .catch((err) => {
          console.log("Error: ", err);
          this.isLoading = false;
        });
    },
    getParams() {
      return {
        brandCode: this.brandSelected ? this.brandSelected.id : null,
        storeCode: this.storeSelected ? this.storeSelected.id : null,
        bookingStatus: this.statusSelected ? this.statusSelected.id : null,
        roomId: this.roomSelected ? this.roomSelected.id : null,
        staffId: this.staffSelected ? this.staffSelected.id : null,
        bookingDate: this.date,
        key: this.keyword,
        limit: this.perPage,
        currentPage: this.currentPage,
        sortColumn: this.sort.key || "id",
        sortType: this.sort.type || "desc",
      };
    },
    listStoredParams() {
      return {
        brandSelected: this.brandSelected,
        storeSelected: this.storeSelected,
        statusSelected: this.statusSelected,
        roomSelected: this.roomSelected,
        staffSelected: this.staffSelected,
        date: this.date,
        keyword: this.keyword,
        limit: this.perPage,
        sortColumn: this.sort.key || "",
        sortType: this.sort.type || "",
      };
    },
    searchBookings() {
      this.getBookings();
    },
    resetFilter() {
      if (this.brandItems !== 1) this.brandSelected = null;
      if (this.storeItems !== 1) this.storeSelected = null;
      this.statusSelected = null;
      this.roomSelected = null;
      this.staffSelected = null;
      this.date = null;
      this.keyword = null;
    },
    changePage() {
      this.getBookings();
    },
    onChangeEntries() {
      this.getBookings();
    },
    sortCompare(e) {
      this.sort.key = e.sortBy;
      this.sort.type = e.sortDesc ? "desc" : "asc";
      this.getBookings();
    },
    setStoresLoading(isLoading) {
      this.isStoreLoading = isLoading;
    },
    setBrand(brand) {
      this.brandSelected = brand || null;
      let storedParams =
        JSON.parse(BookingStorageService.getStoredParams()) || null;
      if (storedParams && this.isMounted) {
        this.storeSelected = storedParams.storeSelected || null;
        this.roomSelected = storedParams.roomSelected || null;
        this.staffSelected = storedParams.staffSelected || null;
      } else {
        this.storeSelected = null;
        this.roomSelected = null;
        this.staffSelected = null;
      }
    },
    setBrands(brands) {
      this.brandItems = brands.length;
      brands.forEach((brand) => {
        if (brands.length === 1) {
          this.brandSelected = brand;
        }
      });
    },
    setStore(store) {
      this.storeSelected = store || null;
      let storedParams =
        JSON.parse(BookingStorageService.getStoredParams()) || null;
      if (storedParams && this.isMounted) {
        this.roomSelected = storedParams.roomSelected || null;
        this.staffSelected = storedParams.staffSelected || null;
        this.isMounted = false;
      } else {
        this.roomSelected = null;
        this.staffSelected = null;
      }
    },
    setStores(stores) {
      this.storeItems = stores.length;
      stores.forEach((store) => {
        if (stores.length === 1) {
          this.storeSelected = store;
        }
      });
    },
    setStatus(status) {
      this.statusSelected = status;
    },
    setRoom(room) {
      this.roomSelected = room;
    },
    setRoomsLoading(isLoading) {
      this.isRoomLoading = isLoading;
    },
    setStaff(staff) {
      this.staffSelected = staff;
    },
    setStaffsLoading(isLoading) {
      this.isStoreLoading = isLoading;
    },
  },
  watch: {
    currentPage(newPage, oldPage) {
      if (newPage !== oldPage) {
        BookingStorageService.setStoredCurrentPage(newPage);
        this.getBookings();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.centered {
  position: absolute;
  top: 45%;
  left: 50%;
}

.booking-loading > :not(:first-child) {
  pointer-events: none;
  opacity: 0.4;
}
</style>
